import { useEffect, useRef, useCallback } from "react"
import styled from "styled-components"
import { ProofOfOwnership } from "~shapes/proofOfOwnership"
import ProofOfOwnershipListItem from "~components/proofOfOwnership/ProofOfOwnershipListItem"
import colors from "~constants/colors"

export interface Props {
  list: ProofOfOwnership[] | undefined
  isLoading: boolean
  selectedStatement: ProofOfOwnership
  setStatement: (v: ProofOfOwnership) => void
  // Controlled open state — the list is opened from the active filter pill.
  isOpen: boolean
  onClose: () => void
  // Clicks inside this container (the pills + the panel) don't close the list,
  // so the toggling pill doesn't immediately re-close it.
  containerRef?: React.RefObject<HTMLElement>
  // Which edge the panel anchors to (right keeps it on-screen under a control
  // that sits near the right edge, e.g. the global tab).
  align?: "left" | "right"
  onLoadMore?: () => void
  hasMore?: boolean
  isLoadingMore?: boolean
  // Show the "who is validating" chip on rows — only in the global Validating
  // list, not in My validations (where every row is the caller's own).
  showAssignee?: boolean
}

// The list of POOs for the active filter, shown as a dropdown anchored under the
// pipeline pills. The panel is absolutely positioned, so its parent in the
// header must be `position: relative`.
const ProofOfOwnershipCollapsibleList = ({
  list,
  isLoading,
  selectedStatement,
  setStatement,
  isOpen,
  onClose,
  containerRef,
  align = "left",
  onLoadMore,
  hasMore,
  isLoadingMore,
  showAssignee,
}: Props) => {
  const scrollRef = useRef<HTMLDivElement>(null)
  const sentinelRef = useRef<HTMLDivElement>(null)
  const loadingRef = useRef(false)
  const existPoo = !!list && list.length > 0

  useEffect(() => {
    if (existPoo && (!selectedStatement || selectedStatement.id === "")) {
      setStatement(list[0])
    }
  }, [existPoo])

  // Close on click outside the pills + panel container.
  useEffect(() => {
    if (!isOpen) return
    const handler = (e: MouseEvent) => {
      const root = containerRef?.current
      if (root && !root.contains(e.target as Node)) onClose()
    }
    document.addEventListener("mousedown", handler)
    return () => document.removeEventListener("mousedown", handler)
  }, [isOpen, containerRef, onClose])

  useEffect(() => {
    if (!isLoadingMore) {
      loadingRef.current = false
    }
  }, [isLoadingMore])

  const handleLoadMore = useCallback(() => {
    if (!hasMore || isLoadingMore || loadingRef.current || !onLoadMore) return
    loadingRef.current = true
    onLoadMore()
  }, [hasMore, isLoadingMore, onLoadMore])

  const handleScroll = useCallback(() => {
    const el = scrollRef.current
    if (!el) return
    const nearBottom = el.scrollHeight - el.scrollTop - el.clientHeight < 100
    if (nearBottom) handleLoadMore()
  }, [handleLoadMore])

  // IntersectionObserver rooted in the ScrollArea so visibility is measured
  // against the scrollable container, not the viewport.
  useEffect(() => {
    const sentinel = sentinelRef.current
    const root = scrollRef.current
    if (!sentinel || !root || !isOpen) return

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          handleLoadMore()
        }
      },
      { root, rootMargin: "100px" },
    )
    observer.observe(sentinel)
    return () => observer.disconnect()
  }, [isOpen, handleLoadMore])

  if (isLoading || !isOpen || !existPoo) return null

  return (
    <DropdownPanel $align={align}>
      <ScrollArea ref={scrollRef} onScroll={handleScroll}>
        {list.map(el => (
          <ProofOfOwnershipListItem
            key={el.id}
            selectedItem={selectedStatement}
            onClick={item => {
              setStatement(item)
              onClose()
            }}
            item={el}
            showAssignee={showAssignee}
          />
        ))}
        {isLoadingMore && <LoadingMore>Loading more...</LoadingMore>}
        {!hasMore && list.length > 0 && <EndOfList>All items loaded</EndOfList>}
        <div ref={sentinelRef} style={{ height: 1 }} />
      </ScrollArea>
    </DropdownPanel>
  )
}

const DropdownPanel = styled.div<{ $align: "left" | "right" }>`
  position: absolute;
  top: calc(100% + 4px);
  left: ${props => (props.$align === "right" ? "auto" : "0")};
  right: ${props => (props.$align === "right" ? "0" : "auto")};
  z-index: 1000;
  min-width: 320px;
  max-width: 400px;
  background: ${colors.white};
  border: 1px solid ${colors.gray2};
  border-radius: 10px;
  box-shadow: 0 8px 24px rgba(0, 0, 0, 0.12);
  overflow: hidden;
`

const ScrollArea = styled.div`
  max-height: 400px;
  overflow-y: auto;
  padding: 6px;
`

const LoadingMore = styled.div`
  text-align: center;
  padding: 12px;
  font-size: 12px;
  color: ${colors.gray6};
`

const EndOfList = styled.div`
  text-align: center;
  padding: 8px;
  font-size: 11px;
  color: ${colors.gray7};
`

export default ProofOfOwnershipCollapsibleList
